import { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { enMessages } from "../../../messages/en-US";
import { useLocalization } from '@progress/kendo-react-intl';
import { useWindowSize } from "usehooks-ts";
import { useToast } from "@chakra-ui/react";
import AssetTemplateTable from "../../Templates/Components/Tables/AssetTemplateTable";
import LoadingSpinner from "../../Shared/Components/LoadingSpinner";
import { ReactQueryKeys, ClientRoutes } from "../../../Constants/ClientRoutingConstants";
import { useQuery, useQueryClient } from "react-query";
import templatesApiService from "../../Templates/Utilities/TemplatesApiService";
import AssetTemplateListItemDto from "../../../Models/Assets/AssetTemplateListItemDto";
import useIdentityStore from "../../../Zustand/identityStore";

export default function SuperAdministrationAssetTemplatesPage(): ReactElement {
  const localizationService = useLocalization();
  const queryClient = useQueryClient();
  const userRole = useIdentityStore((store) => store.user?.profile?.role);
  const { height } = useWindowSize();
  const toast = useToast({
    position: "bottom",
    duration: 5000,
    isClosable: true,
  });

  const assetTemplatesQuery = useQuery<AssetTemplateListItemDto[]>(
    [ReactQueryKeys.AllTemplatesQueryAll],
    () => templatesApiService.getTemplatesAssetsAll(),
    { enabled: userRole === "SuperAdmin" }
  );

  if (userRole !== "SuperAdmin") {
    return <Navigate to={ClientRoutes.Home} replace />;
  }

  if (assetTemplatesQuery.isLoading) {
    return (
      <div className="m-5 flex flex-grow items-center justify-center min-h-[200px]" id="AssetTemplatesPage">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="m-5" id="AssetTemplatesPage">
      <div>
        <h1 className="gb-h1">{localizationService.toLanguageString('custom.manageAssetTemplates', enMessages.custom.manageAssetTemplates)}</h1>
        <h2 className="gb-text-normal mb-6">{localizationService.toLanguageString('custom.manageAssetTemplatesText', enMessages.custom.manageAssetTemplatesText)}</h2>
      </div>
      <AssetTemplateTable
        height={height - 200}
        data={assetTemplatesQuery.data || []}
        isAdministrationMode={true}
        onTemplateSaved={() => {
          assetTemplatesQuery.refetch();
          queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.SelectedAssetQuery] });
          toast({
            title: localizationService.toLanguageString('custom.success', enMessages.custom.success),
            description: localizationService.toLanguageString('custom.assetTemplateSaved', enMessages.custom.assetTemplateSaved),
            status: "success",
          });
        }}
        onTemplateDeleted={() => {
          assetTemplatesQuery.refetch();
          toast({
            title: localizationService.toLanguageString('custom.success', enMessages.custom.success),
            description: localizationService.toLanguageString('custom.assetTemplateDeleted', enMessages.custom.assetTemplateDeleted),
            status: "success",
          });
        }}
      />
    </div>
  );
}
